import React, { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';

interface VendorAddress {
  vendor_address_id: string | null;
  name: string | null;
  alt_address_status: string | null;
  alt_vendor_key: string | null;
  address: {
    street: string | null;
    city: string | null;
    state: string | null;
    postal_code: string | null;
    full_address: string | null;
  };
}

interface Props {
  vendorNumber: string | undefined;
  userId: number | undefined;
  currentAddressId: string | undefined;
  onChange: (address: {
    vendor_address_id: string;
    full_address: string | null;
    name: string | null;
  }) => void;
}

const prefKey = (userId: number, vendorNumber: string) =>
  `keyscan.vendor_address_pref.${userId}.${vendorNumber}`;

const composeFullAddress = (a: VendorAddress['address']): string | null => {
  if (a.full_address) return a.full_address;
  const parts = [a.street, a.city, a.state, a.postal_code].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
};

const optionLabel = (a: VendorAddress): string => {
  const alias = a.name ? ` · ${a.name}` : '';
  const line = a.address.street ? ` · ${a.address.street}` : '';
  const status = a.alt_address_status ? ` (${a.alt_address_status})` : '';
  return `${a.vendor_address_id ?? '(no id)'}${alias}${line}${status}`;
};

const VendorAddressSelect: React.FC<Props> = ({
  vendorNumber,
  userId,
  currentAddressId,
  onChange,
}) => {
  const [addresses, setAddresses] = useState<VendorAddress[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Latest props stashed in refs so the fetch effect only depends on
  // vendorNumber / userId. Otherwise a parent re-render (which recreates
  // onChange) would re-fire the fetch on every keystroke.
  const onChangeRef = useRef(onChange);
  const currentAddressIdRef = useRef(currentAddressId);
  onChangeRef.current = onChange;
  currentAddressIdRef.current = currentAddressId;

  useEffect(() => {
    if (!vendorNumber) {
      setAddresses(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/vendor/${encodeURIComponent(vendorNumber)}/addresses`)
      .then((res) => {
        if (cancelled) return;
        const list: VendorAddress[] = res.data?.addresses || [];
        setAddresses(list);
        // Apply the user's remembered preference for THIS vendor if it
        // still exists in the returned list; otherwise fall through to
        // the server-sorted default (list[0]).
        if (!currentAddressIdRef.current && list.length > 0 && userId !== undefined) {
          const remembered = localStorage.getItem(prefKey(userId, vendorNumber));
          const pick =
            (remembered && list.find((a) => a.vendor_address_id === remembered)) ||
            list[0];
          if (pick?.vendor_address_id) {
            onChangeRef.current({
              vendor_address_id: pick.vendor_address_id,
              full_address: composeFullAddress(pick.address),
              name: pick.name,
            });
          }
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail || err?.message || 'Failed to load addresses';
        setError(String(detail));
        setAddresses(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [vendorNumber, userId]);

  const handlePick = (vid: string) => {
    const pick = addresses?.find((a) => a.vendor_address_id === vid);
    if (!pick?.vendor_address_id) return;
    // Remember this pick as the default for future invoices from this vendor
    // (only if it differs from the server's recommended first option).
    if (userId !== undefined && vendorNumber && addresses && addresses.length > 0) {
      if (pick.vendor_address_id !== addresses[0].vendor_address_id) {
        localStorage.setItem(prefKey(userId, vendorNumber), pick.vendor_address_id);
      } else {
        // User picked the default — clear any prior remembered pick
        localStorage.removeItem(prefKey(userId, vendorNumber));
      }
    }
    onChange({
      vendor_address_id: pick.vendor_address_id,
      full_address: composeFullAddress(pick.address),
      name: pick.name,
    });
  };

  if (!vendorNumber) {
    return (
      <input
        type="text"
        value={currentAddressId || ''}
        readOnly
        placeholder="Look up a vendor first"
        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
      />
    );
  }

  if (loading) {
    return (
      <div className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500">
        Loading addresses…
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full px-3 py-2 text-sm border border-red-200 dark:border-red-800 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
        {error}
      </div>
    );
  }

  if (!addresses || addresses.length === 0) {
    return (
      <input
        type="text"
        value={currentAddressId || ''}
        onChange={(e) => onChange({ vendor_address_id: e.target.value, full_address: null, name: null })}
        placeholder="No addresses on file"
        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
      />
    );
  }

  if (addresses.length === 1) {
    const only = addresses[0];
    return (
      <div className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
        {only.vendor_address_id}
        {only.name ? ` · ${only.name}` : ''}
        {only.alt_address_status ? ` (${only.alt_address_status})` : ''}
      </div>
    );
  }

  return (
    <select
      value={currentAddressId || addresses[0]?.vendor_address_id || ''}
      onChange={(e) => handlePick(e.target.value)}
      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white"
      title="Multiple addresses on file. Default is the check address (status C) or the first available; your selection is remembered for future invoices from this vendor."
    >
      {addresses.map((a) => (
        <option key={a.vendor_address_id || ''} value={a.vendor_address_id || ''}>
          {optionLabel(a)}
        </option>
      ))}
    </select>
  );
};

export default VendorAddressSelect;
